import { useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Alert,
  Autocomplete,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Modal,
  NumberInput,
  Pagination,
  Paper,
  ScrollArea,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import {
  IconAlertTriangle,
  IconArrowDownRight,
  IconArrowUpRight,
  IconCategory,
  IconHistory,
  IconPackage,
  IconPencil,
  IconPlus,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addStockMovement,
  createSupplyItem,
  deleteSupplyItem,
  getStockMovements,
  getSupplyCategories,
  getSupplyItems,
  updateSupplyItem,
} from "@/api/supply";
import {
  ListCell,
  ListEmpty,
  ListError,
  ListHeader,
  ListPagination,
  ListRow,
  ListScroll,
  ListSkeleton,
} from "@/components/DataList";
import { FormModalFooter } from "@/components/FormModalFooter";
import { ModalTitle } from "@/components/ModalTitle";
import { PageTitle } from "@/components/PageTitle";
import { useAccess } from "@/hooks/useAccess";
import { toIsoDay } from "@/lib/format";
import { notify } from "@/lib/notify";
import { lockProps } from "@/lib/permissions";
import { CategoryManager } from "./CategoryManager";

const ITEM_COLUMNS = [
  { label: "Kalem Adı", flex: true, ta: "left" },
  { label: "Kategori", w: 170 },
  { label: "Birim", w: 90 },
  { label: "Stok", w: 120 },
  { label: "İşlem", w: 140 },
];

const REASON_META = {
  intake: { label: "Mal kabul", color: "teal" },
  transfer_out: { label: "Şubeye sevk", color: "blue" },
  transfer_in: { label: "Şubeye giriş", color: "blue" },
  waste: { label: "Fire", color: "red" },
  adjustment: { label: "Sayım düzeltmesi", color: "gray" },
};

// Elle girilebilen hareketler. Transfer BURADA YOK: onu talep akışı yazıyor, elle de
// yazılabilseydi defter ile talepler birbirini tutmazdı.
const REASONS = ["intake", "waste", "adjustment"].map((value) => ({
  value,
  label: REASON_META[value].label,
}));

// Birim serbest yazılıyor; bunlar yalnızca öneri, partner kendi birimini ("çuval", "teneke")
// yazabilsin diye liste kapatılmadı.
const UNITS = ["adet", "kg", "g", "lt", "ml", "koli", "paket", "kutu", "tepsi"];

const NOTE_PLACEHOLDER = {
  intake: "İrsaliye no",
  waste: "Fire sebebi",
  adjustment: "Sayım farkının gerekçesi",
};

const DAY_FORMAT = new Intl.DateTimeFormat("tr-TR", {
  day: "numeric",
  month: "long",
  weekday: "long",
});
const TIME_FORMAT = new Intl.DateTimeFormat("tr-TR", {
  hour: "2-digit",
  minute: "2-digit",
});

const PAGE_SIZE = 25;

const TR = new Intl.Collator("tr");

// Takvimde seçilen gün kullanıcının saat diliminde başlayıp bitiyor; sınırları burada epoch ms'e
// çevirip gönderiyoruz ki sunucunun saat dilimi sonucu kaydırmasın.
const dayBound = (value, time) => {
  const iso = toIsoDay(value);
  return iso ? new Date(`${iso}T${time}`).getTime() : undefined;
};

export default function SupplyItems() {
  const queryClient = useQueryClient();
  const { can } = useAccess();
  const [itemModal, item] = useDisclosure(false);
  const [stockModal, stock] = useDisclosure(false);
  const [categoryModal, categoryManager] = useDisclosure(false);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [stockFor, setStockFor] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [direction, setDirection] = useState("");
  const [movePage, setMovePage] = useState(1);
  const [range, setRange] = useState([null, null]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const items = useQuery({
    queryKey: ["supply-items"],
    queryFn: getSupplyItems,
  });
  const categories = useQuery({
    queryKey: ["supply-categories"],
    queryFn: getSupplyCategories,
  });

  const categoryById = useMemo(
    () =>
      new Map(
        (categories.data ?? []).map((category) => [category.id, category.name]),
      ),
    [categories.data],
  );

  const categoryOptions = useMemo(
    () =>
      (categories.data ?? []).map((category) => ({
        value: String(category.id),
        label: category.name,
      })),
    [categories.data],
  );

  const itemCount = (categoryId) =>
    (items.data ?? []).filter((row) => row.categoryId === categoryId).length;

  const from = dayBound(range[0], "00:00:00");
  // Tek gün seçiliyken bitiş de o gün: aralık seçici ilk tıklamada yalnız başlangıcı doldurur.
  const to = dayBound(range[1] ?? range[0], "23:59:59.999");

  const movements = useQuery({
    queryKey: [
      "supply-movements",
      historyFor?.id,
      direction,
      from,
      to,
      movePage,
    ],
    queryFn: () =>
      getStockMovements(historyFor.id, {
        limit: PAGE_SIZE,
        offset: (movePage - 1) * PAGE_SIZE,
        direction: direction || undefined,
        from,
        to,
      }),
    enabled: Boolean(historyFor),
    // Sayfa değişince liste boşalıp sıçramasın; eski sayfa yenisi gelene kadar duruyor.
    placeholderData: (previous) => previous,
  });

  const moveTotal = movements.data?.total ?? 0;
  const movePages = Math.max(1, Math.ceil(moveTotal / PAGE_SIZE));

  // Aynı güne düşen hareketler tek başlık altında toplanıyor; yüz satırlık bir defterde
  // her satıra tarih basmak listeyi okunmaz hâle getiriyor.
  const days = useMemo(() => {
    const groups = [];

    for (const row of movements.data?.rows ?? []) {
      const day = DAY_FORMAT.format(new Date(row.createdAt));
      const last = groups.at(-1);

      if (last?.day === day) {
        last.rows.push(row);
      } else {
        groups.push({ day, rows: [row] });
      }
    }

    return groups;
  }, [movements.data]);

  // SQLite'ın collation'ı Türkçe bilmiyor: ç/ğ/ı ile başlayan kalemler listenin sonuna
  // düşüyor. Sıralama burada ve yalnızca liste değişince — her tuşta yeniden sıralamak
  // collator'ın yavaş yolunu tetikliyordu.
  const sorted = useMemo(
    () => [...(items.data ?? [])].sort((a, b) => TR.compare(a.name, b.name)),
    [items.data],
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();

    return sorted.filter((row) => {
      if (categoryFilter === "none" && categoryById.has(row.categoryId)) {
        return false;
      }
      if (
        categoryFilter !== "all" &&
        categoryFilter !== "none" &&
        String(row.categoryId) !== categoryFilter
      ) {
        return false;
      }
      return term ? row.name.toLowerCase().includes(term) : true;
    });
  }, [sorted, search, categoryFilter, categoryById]);

  useEffect(() => {
    setPage(1);
  }, [search, categoryFilter]);

  useEffect(() => {
    setMovePage(1);
  }, [historyFor, direction, from, to]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pageItems = filtered.slice((page - 1) * perPage, page * perPage);

  const itemForm = useForm({
    initialValues: {
      name: "",
      unit: "adet",
      note: "",
      categoryId: "",
      isActive: true,
    },
    validate: {
      name: (value) => (value.trim() ? null : "Kalem adı gerekli"),
      unit: (value) => (value.trim() ? null : "Birim gerekli"),
    },
  });

  const stockForm = useForm({
    initialValues: { qty: 0, reason: "intake", note: "" },
    validate: { qty: (value) => (value === 0 ? "Miktar sıfır olamaz" : null) },
  });

  // Birimi değişen kalemin geçmiş hareketleri eski birimde yazılmıştı; kullanıcı bunu görmeden
  // kaydetmesin.
  const unitChanged =
    Boolean(editing) && itemForm.values.unit !== editing?.unit;

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["supply-items"] });

  const saveItem = useMutation({
    mutationFn: ({ categoryId, ...values }) => {
      const payload = {
        ...values,
        categoryId: categoryId ? Number(categoryId) : null,
      };

      return editing
        ? updateSupplyItem(editing.id, payload)
        : createSupplyItem(payload);
    },
    onSuccess: () => {
      refresh();
      item.close();
      notify.success(editing ? "Kalem güncellendi" : "Kalem eklendi");
    },
    onError: (error) => notify.error(error),
  });

  const removeItem = useMutation({
    mutationFn: deleteSupplyItem,
    onSuccess: (data) => {
      refresh();
      notify.success(data?.archived ? "Kalem arşivlendi" : "Kalem silindi");
    },
    onError: (error) => notify.error(error),
  });

  const saveStock = useMutation({
    mutationFn: (values) =>
      addStockMovement({ itemId: stockFor.id, ...values }),
    onSuccess: (data) => {
      // Yanıt yeni bakiyeyi taşıyor; listeyi baştan çekmek bütün defteri yeniden topluyordu.
      queryClient.setQueryData(["supply-items"], (rows) =>
        rows?.map((row) =>
          row.id === data.itemId ? { ...row, stock: data.stock } : row,
        ),
      );
      queryClient.invalidateQueries({
        queryKey: ["supply-movements", data.itemId],
      });
      stock.close();
      notify.success("Stok hareketi işlendi");
    },
    onError: (error) => notify.error(error),
  });

  const openItem = (row) => {
    setEditing(row);
    if (row) {
      itemForm.setValues({
        name: row.name,
        unit: row.unit,
        note: row.note ?? "",
        categoryId: row.categoryId ? String(row.categoryId) : "",
        isActive: row.isActive,
      });
    } else {
      itemForm.reset();
    }
    item.open();
  };

  const openStock = (row) => {
    setStockFor(row);
    stockForm.reset();
    stock.open();
  };

  const openHistory = (row) => {
    const today = toIsoDay(new Date());

    setDirection("");
    setRange([today, today]);
    setMovePage(1);
    setHistoryFor(row);
  };

  const confirmDelete = (row) =>
    modals.openConfirmModal({
      title: <ModalTitle icon={IconTrash} title="Kalemi sil" />,
      children: (
        <Text size="sm">
          Hiç kullanılmamışsa <strong>{row.name}</strong> tamamen silinir. Stok
          hareketi ya da geçmiş talebi varsa kalem arşivlenir: listeden düşer
          ama eski kayıtlarda görünmeye devam eder.
        </Text>
      ),
      labels: { confirm: "Sil", cancel: "Vazgeç" },
      confirmProps: { color: "red" },
      onConfirm: () => removeItem.mutate(row.id),
    });

  return (
    <Stack gap="lg">
      <PageTitle>İkmal Kalemleri</PageTitle>
      <Group justify="space-between">
        <Group>
          <ThemeIcon size={46} radius="md" variant="light" color="chocolate">
            <IconPackage size={26} stroke={1.6} />
          </ThemeIcon>
          <div>
            <Title order={2}>İkmal Kalemleri</Title>
            <Text c="dimmed" size="sm">
              Merkez deposunun kalemlerini tanımla, stok hareketlerini işle
            </Text>
          </div>
        </Group>
        {/* Yetkisiz kullanıcıda gizlenmiyor, kilitleniyor. */}
        <Group gap="sm">
          <Button
            variant="default"
            leftSection={<IconCategory size={16} />}
            {...lockProps(can("supply_center"))}
            onClick={categoryManager.open}
          >
            Kategoriler
          </Button>
          <Button
            leftSection={<IconPlus size={16} />}
            {...lockProps(can("supply_center"))}
            onClick={() => openItem(null)}
          >
            Kalem ekle
          </Button>
        </Group>
      </Group>

      <Card withBorder radius="md" p="md">
        <Group justify="center" mb="md">
          <TextInput
            w={260}
            placeholder="Kalem adı ara"
            leftSection={<IconSearch size={15} />}
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
          <Select
            w={220}
            allowDeselect={false}
            value={categoryFilter}
            onChange={(value) => setCategoryFilter(value ?? "all")}
            data={[
              { value: "all", label: "Tüm kategoriler" },
              ...categoryOptions,
              { value: "none", label: "Kategorisiz" },
            ]}
          />
        </Group>

        <Card withBorder radius="md" p={0}>
          {items.isLoading ? (
            <ListScroll minWidth={830}>
              <ListHeader columns={ITEM_COLUMNS} />
              <ListSkeleton
                columns={ITEM_COLUMNS}
                rows={perPage > 10 ? 10 : perPage}
              />
            </ListScroll>
          ) : items.isError ? (
            <ListError
              title="İkmal kalemleri alınamadı"
              onRetry={items.refetch}
            />
          ) : (
            <>
              <ListScroll minWidth={830}>
                <ListHeader columns={ITEM_COLUMNS} />

                {pageItems.length === 0 ? (
                  <ListEmpty>
                    {search ? "Kalem bulunamadı" : "Henüz ikmal kalemi yok"}
                  </ListEmpty>
                ) : (
                  pageItems.map((row) => (
                    <ListRow key={row.id}>
                      <ListCell flex ta="left">
                        <Group gap={6} wrap="nowrap">
                          <Text
                            fw={500}
                            c={row.isActive ? undefined : "dimmed"}
                            truncate
                          >
                            {row.name}
                          </Text>
                          {row.isActive ? null : (
                            <Badge
                              size="xs"
                              variant="light"
                              tt="none"
                              color="gray"
                            >
                              Arşivli
                            </Badge>
                          )}
                        </Group>
                        {row.note ? (
                          <Text size="xs" c="dimmed" truncate>
                            {row.note}
                          </Text>
                        ) : null}
                      </ListCell>
                      <ListCell w={170}>
                        <Text
                          size="sm"
                          c={
                            categoryById.has(row.categoryId)
                              ? undefined
                              : "dimmed"
                          }
                          truncate
                        >
                          {categoryById.get(row.categoryId) ?? "—"}
                        </Text>
                      </ListCell>
                      <ListCell w={90}>{row.unit}</ListCell>
                      <ListCell w={120}>
                        <Badge
                          variant="light"
                          color={row.stock > 0 ? "teal" : "gray"}
                        >
                          {row.stock}
                        </Badge>
                      </ListCell>
                      <ListCell w={140}>
                        <Group gap={4} justify="center" wrap="nowrap">
                          <Tooltip label="Düzenle">
                            <ActionIcon
                              variant="subtle"
                              onClick={() => openItem(row)}
                            >
                              <IconPencil size={16} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label="Stok hareketi">
                            <ActionIcon
                              variant="subtle"
                              onClick={() => openStock(row)}
                            >
                              <IconPlus size={16} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label="Hareket geçmişi">
                            <ActionIcon
                              variant="subtle"
                              onClick={() => openHistory(row)}
                            >
                              <IconHistory size={16} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label="Sil">
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              onClick={() => confirmDelete(row)}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </ListCell>
                    </ListRow>
                  ))
                )}
              </ListScroll>

              <ListPagination
                page={page}
                onPage={setPage}
                perPage={perPage}
                onPerPage={(value) => {
                  setPerPage(value);
                  setPage(1);
                }}
                total={filtered.length}
                unit="kalem"
                totalPages={totalPages}
              />
            </>
          )}
        </Card>
      </Card>

      <CategoryManager
        opened={categoryModal}
        onClose={categoryManager.close}
        categories={categories.data ?? []}
        itemCount={itemCount}
      />

      <Modal
        opened={itemModal}
        onClose={item.close}
        title={
          <ModalTitle
            icon={IconPackage}
            title={editing ? "Kalemi düzenle" : "Kalem ekle"}
          />
        }
      >
        <form onSubmit={itemForm.onSubmit((values) => saveItem.mutate(values))}>
          <Stack gap="md">
            {editing ? (
              <Paper
                withBorder
                radius="md"
                p="sm"
                bg="var(--mantine-color-gray-0)"
              >
                <Group justify="space-between" wrap="nowrap">
                  <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                    <ThemeIcon
                      size={38}
                      radius="md"
                      variant="light"
                      color="chocolate"
                    >
                      <IconPackage size={20} stroke={1.6} />
                    </ThemeIcon>
                    <div style={{ minWidth: 0 }}>
                      <Text fw={600} truncate>
                        {editing.name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {editing.isActive
                          ? "Şubelerin talep listesinde"
                          : "Arşivde"}
                      </Text>
                    </div>
                  </Group>
                  <Badge
                    size="lg"
                    variant="light"
                    tt="none"
                    color={editing.stock > 0 ? "teal" : "gray"}
                  >
                    {editing.stock} {editing.unit}
                  </Badge>
                </Group>
              </Paper>
            ) : null}

            <TextInput
              label="Kalem adı"
              placeholder="Kuvertür %70"
              data-autofocus
              {...itemForm.getInputProps("name")}
            />

            <Select
              label="Kategori"
              placeholder={
                categoryOptions.length
                  ? "Kategori seç"
                  : "Önce Kategoriler’den ekle"
              }
              clearable
              data={categoryOptions}
              {...itemForm.getInputProps("categoryId")}
            />

            {/* Yan yana iki alanın yalnızca birinde description varken altları
                            hizalanmıyordu; açıklama artık placeholder'da. */}
            <Group align="flex-start" gap="sm" wrap="nowrap">
              <Autocomplete
                label="Birim"
                w={150}
                data={UNITS}
                placeholder="kg, adet, koli"
                {...itemForm.getInputProps("unit")}
              />
              <TextInput
                label="Açıklama"
                placeholder="Marka, gramaj, tedarikçi"
                style={{ flex: 1 }}
                {...itemForm.getInputProps("note")}
              />
            </Group>

            {editing ? null : (
              <Text size="xs" c="dimmed">
                Kalem sıfır stokla açılır. Mal kabulünü listedeki + düğmesinden
                girersin.
              </Text>
            )}

            {unitChanged ? (
              <Alert
                color="yellow"
                variant="light"
                icon={<IconAlertTriangle size={16} />}
                title="Birim değişiyor"
              >
                Geçmiş hareketler {editing.unit} olarak yazıldı. Sayılar olduğu
                gibi kalır, yalnızca birim etiketi değişir — {editing.stock}{" "}
                {editing.unit} artık {editing.stock} {itemForm.values.unit}{" "}
                olarak okunur.
              </Alert>
            ) : null}

            {editing ? (
              <>
                <Divider />
                <Switch
                  label="Şubelerin talep listesinde göster"
                  description="Kapalıyken kalem arşivde kalır; geçmiş hareketleri ve talepleri durur."
                  {...itemForm.getInputProps("isActive", { type: "checkbox" })}
                />
              </>
            ) : null}

            <FormModalFooter>
              <Button variant="default" onClick={item.close}>
                Vazgeç
              </Button>
              <Button type="submit" loading={saveItem.isPending}>
                {editing ? "Kaydet" : "Ekle"}
              </Button>
            </FormModalFooter>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={stockModal}
        onClose={stock.close}
        title={
          <ModalTitle
            icon={IconPlus}
            title={`Stok hareketi — ${stockFor?.name ?? ""}`}
          />
        }
      >
        <form
          onSubmit={stockForm.onSubmit((values) => saveStock.mutate(values))}
        >
          <Stack gap="sm">
            <Select
              label="Hareket"
              data={REASONS}
              allowDeselect={false}
              {...stockForm.getInputProps("reason")}
            />
            <NumberInput
              label="Miktar"
              description="Azaltmak için eksi değer girin."
              suffix={stockFor ? ` ${stockFor.unit}` : ""}
              {...stockForm.getInputProps("qty")}
            />
            <TextInput
              label="Not"
              placeholder={NOTE_PLACEHOLDER[stockForm.values.reason]}
              {...stockForm.getInputProps("note")}
            />
            <FormModalFooter>
              <Button variant="default" onClick={stock.close}>
                Vazgeç
              </Button>
              <Button type="submit" loading={saveStock.isPending}>
                İşle
              </Button>
            </FormModalFooter>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={Boolean(historyFor)}
        onClose={() => setHistoryFor(null)}
        title={
          <ModalTitle
            icon={IconHistory}
            title={`Hareketler — ${historyFor?.name ?? ""}`}
          />
        }
        size="lg"
      >
        <Stack gap="sm">
          <Group justify="space-between" gap="sm" wrap="wrap">
            <Badge
              size="lg"
              variant="light"
              tt="none"
              color={historyFor?.stock > 0 ? "teal" : "gray"}
            >
              Mevcut stok: {historyFor?.stock ?? 0} {historyFor?.unit}
            </Badge>
            <Group gap="xs" wrap="nowrap">
              <DatePickerInput
                aria-label="Tarih aralığı"
                type="range"
                size="xs"
                w={210}
                clearable
                valueFormat="D MMM YYYY"
                placeholder="Tüm tarihler"
                value={range}
                onChange={setRange}
              />
              <SegmentedControl
                size="xs"
                value={direction}
                onChange={setDirection}
                data={[
                  { value: "", label: "Tümü" },
                  { value: "in", label: "Giriş" },
                  { value: "out", label: "Çıkış" },
                ]}
              />
            </Group>
          </Group>

          <Divider />

          {movements.isPending ? (
            <Text size="sm" c="dimmed" py="lg" ta="center">
              Yükleniyor…
            </Text>
          ) : movements.isError ? (
            <ListError
              title="Hareketler alınamadı"
              onRetry={movements.refetch}
            />
          ) : !days.length ? (
            <Stack gap="xs" align="center" py="lg">
              <Text size="sm" c="dimmed">
                {from || direction
                  ? "Seçilen filtrede hareket yok."
                  : "Bu kalemde hareket yok."}
              </Text>
              {from || direction ? (
                <Button
                  size="xs"
                  variant="default"
                  onClick={() => {
                    setRange([null, null]);
                    setDirection("");
                  }}
                >
                  Tüm hareketleri göster
                </Button>
              ) : null}
            </Stack>
          ) : (
            <ScrollArea.Autosize mah={420} type="auto" offsetScrollbars>
              <Stack gap={0}>
                {days.map((group) => (
                  <Box key={group.day}>
                    <Text
                      size="xs"
                      fw={700}
                      c="dimmed"
                      tt="uppercase"
                      py={6}
                      style={{
                        // Uzun defterlerde hangi güne bakıldığı kaybolmasın diye
                        // gün başlığı kaydırma kutusunun tepesine yapışıyor.
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                        background: "var(--mantine-color-body)",
                      }}
                    >
                      {group.day}
                    </Text>
                    {group.rows.map((row) => (
                      <MovementRow key={row.id} row={row} />
                    ))}
                  </Box>
                ))}
              </Stack>
            </ScrollArea.Autosize>
          )}

          {moveTotal > PAGE_SIZE ? (
            <Group justify="space-between" wrap="nowrap">
              <Text size="xs" c="dimmed">
                {moveTotal} hareket
              </Text>
              <Pagination
                size="sm"
                withEdges
                total={movePages}
                value={movePage}
                onChange={setMovePage}
              />
            </Group>
          ) : null}
        </Stack>
      </Modal>
    </Stack>
  );
}

function MovementRow({ row }) {
  const meta = REASON_META[row.reason] ?? { label: row.reason, color: "gray" };
  const incoming = row.qty > 0;

  return (
    <Group
      gap="sm"
      wrap="nowrap"
      py={8}
      style={{ borderTop: "1px solid var(--mantine-color-gray-1)" }}
    >
      <ThemeIcon
        size="md"
        radius="xl"
        variant="light"
        color={incoming ? "teal" : "red"}
      >
        {incoming ? (
          <IconArrowUpRight size={15} />
        ) : (
          <IconArrowDownRight size={15} />
        )}
      </ThemeIcon>
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Group gap={6} wrap="nowrap">
          <Text size="sm" fw={500}>
            {meta.label}
          </Text>
          {row.requestId ? (
            <Badge size="xs" variant="light" tt="none" color={meta.color}>
              Talep #{row.requestId}
            </Badge>
          ) : null}
        </Group>
        <Text size="xs" c="dimmed" truncate>
          {TIME_FORMAT.format(new Date(row.createdAt))}
          {row.note ? ` · ${row.note}` : ""}
        </Text>
      </Box>
      <Text
        fw={600}
        c={incoming ? "teal" : "red"}
        w={80}
        ta="right"
        ff="monospace"
      >
        {incoming ? "+" : "−"}
        {Math.abs(row.qty)}
      </Text>
    </Group>
  );
}
